import { Popover as RadixPopover } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { cn } from '../lib/cn';

/** Root (Radix Popover). Closes on outside click and Escape. */
export const Popover = RadixPopover.Root;
export const PopoverTrigger = RadixPopover.Trigger;
export const PopoverAnchor = RadixPopover.Anchor;
export const PopoverClose = RadixPopover.Close;

export interface PopoverContentProps extends ComponentPropsWithoutRef<typeof RadixPopover.Content> {
  /** Inner padding: none · sm 6px (menus/lists) · md 12px (default) · lg 16px. */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Render without a portal (e.g. inside a scroll container that must clip). */
  inline?: boolean;
}

const PADDING = { none: 'p-0', sm: 'p-1.5', md: 'p-3', lg: 'p-4' } as const;

/** Surface: border, 10px radius, shadow-lg. */
export const PopoverContent = forwardRef<ElementRef<typeof RadixPopover.Content>, PopoverContentProps>(
  function PopoverContent(
    {
      className,
      padding = 'md',
      sideOffset = 6,
      align = 'start',
      collisionPadding = 8,
      inline = false,
      ...rest
    },
    ref,
  ) {
    const content = (
      <RadixPopover.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        collisionPadding={collisionPadding}
        className={cn(
          'z-[60] max-w-[calc(100vw-16px)] rounded-[10px] border border-border bg-surface text-13 text-text shadow-lg outline-none',
          'data-[state=open]:animate-pop-in',
          PADDING[padding],
          className,
        )}
        {...rest}
      />
    );
    return inline ? content : <RadixPopover.Portal>{content}</RadixPopover.Portal>;
  },
);
